import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const empty = { name: "", subject: "", description: "", body: "" };
const MERGE_FIELDS = ["candidate_name", "job_title", "department", "location", "salary", "joining_date", "reporting_manager", "offer_date"];

export default function AdminOfferTemplates() {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/offer-templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const startNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const startEdit = (t) => { setForm(t); setEditing(t.id); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/offer-templates/${editing}`, form);
      else await api.post("/admin/offer-templates", form);
      toast.success("Template saved");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const cls = "w-full rounded-md border border-white/10 bg-[#0A0D14] px-3 py-2.5 font-sans text-sm text-[#F1F5F9] focus:border-gold focus:outline-none";

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <p className="eyebrow">Controlled Documents</p>
          <h1 className="mt-3 font-serif text-3xl tracking-tight">Offer Templates</h1>
          <p className="mt-2 font-sans text-sm text-[#94A3B8]">Offers are generated from these templates only — no freeform editing.</p>
        </div>
        <button onClick={startNew} data-testid="new-template-btn" className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 font-sans text-xs font-semibold uppercase tracking-widest text-[#0A0D14] hover:bg-gold-bright">
          <Plus size={15} /> New Template
        </button>
      </div>

      <div className="mt-8 grid gap-4 md:grid-cols-2">
        {templates.map((t) => (
          <div key={t.id} data-testid={`template-${t.id}`} className="rounded-lg border border-white/5 bg-[#121722] p-6">
            <h3 className="font-serif text-lg">{t.name}</h3>
            <p className="mt-1 font-sans text-sm text-[#94A3B8]">{t.subject}</p>
            <p className="mt-3 font-sans text-xs text-[#94A3B8]">{t.description}</p>
            <button onClick={() => startEdit(t)} data-testid={`template-edit-${t.id}`} className="mt-4 rounded-md border border-white/10 px-4 py-2 font-sans text-xs text-[#94A3B8] hover:border-gold hover:text-gold">Edit</button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-white/10 bg-[#121722] text-[#F1F5F9]">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit Template" : "New Template"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input placeholder="Template name" value={form.name} onChange={set("name")} className={cls} data-testid="template-name" />
            <input placeholder="Subject (supports {{merge_fields}})" value={form.subject} onChange={set("subject")} className={cls} data-testid="template-subject" />
            <input placeholder="Description" value={form.description} onChange={set("description")} className={cls} data-testid="template-description" />
            <div>
              <p className="eyebrow mb-2">Available merge fields (click to insert)</p>
              <div className="flex flex-wrap gap-1.5">
                {MERGE_FIELDS.map((f) => (
                  <button key={f} type="button" onClick={() => setForm((s) => ({ ...s, body: s.body + `{{${f}}}` }))} className="rounded-full border border-white/10 px-2.5 py-1 font-mono text-[10px] text-gold hover:bg-gold/10">{`{{${f}}}`}</button>
                ))}
              </div>
            </div>
            <textarea placeholder="Body (HTML with {{merge_fields}})" value={form.body} onChange={set("body")} rows={12} className={`${cls} font-mono text-xs`} data-testid="template-body" />
            <button onClick={save} data-testid="template-save" className="w-full rounded-full bg-gold px-6 py-3 font-sans text-xs font-semibold uppercase tracking-widest text-[#0A0D14] hover:bg-gold-bright">Save Template</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
